import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  loginOwner,
  registerOwner,
  forgotOwnerPassword,
  clearOwnerError,
} from '@main/features/auth/ownerAuthSlice';
import { UiButton, UiInput, UiLinkButton } from '@shared/common/ui';

const TABS = [
  { id: 'login', label: 'Вход' },
  { id: 'forgot', label: 'Забыл пароль' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const { status, sessionChecked } = useSelector((s) => s.ownerAuth);
  const [tab, setTab] = useState('login');

  useEffect(() => {
    if (sessionChecked && status === 'authenticated') navigate('/profile');
  }, [sessionChecked, status, navigate]);

  return (
    <div className="auth-card">
      <div className="auth-card__tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={tab === t.id ? 'active' : ''}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'login' && <LoginForm />}
      {tab === 'forgot' && <ForgotForm onLogin={() => setTab('login')} />}
    </div>
  );
}

// ----- Login -----

function LoginForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, error } = useSelector((s) => s.ownerAuth);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { email: '', password: '' } });

  useEffect(() => () => dispatch(clearOwnerError()), [dispatch]);

  const onSubmit = async (values) => {
    const result = await dispatch(loginOwner(values));
    if (loginOwner.fulfilled.match(result)) {
      navigate('/profile');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h1>Вход в кабинет</h1>
      <UiInput
        label="Email"
        type="email"
        autoComplete="email"
        error={errors.email?.message}
        {...register('email', {
          required: 'Введите email',
          pattern: { value: /\S+@\S+\.\S+/, message: 'Некорректный email' },
        })}
      />
      <UiInput
        label="Пароль"
        type="password"
        autoComplete="current-password"
        error={errors.password?.message}
        {...register('password', {
          required: 'Введите пароль',
          minLength: { value: 6, message: 'Не меньше 6 символов' },
        })}
      />
      {error && <span className="form-error">{error}</span>}
      <UiButton type="submit" disabled={isSubmitting || status === 'loading'}>
        {isSubmitting || status === 'loading' ? 'Входим…' : 'Войти'}
      </UiButton>
    </form>
  );
}

// ----- Register -----

function RegisterForm({ onLogin }) {
  const dispatch = useDispatch();
  const [infoMessage, setInfoMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { email: '', password: '', passwordRepeat: '' } });

  const onSubmit = async (values) => {
    setInfoMessage(null);
    setErrorMessage(null);
    const result = await dispatch(registerOwner(values));
    if (registerOwner.fulfilled.match(result)) {
      setInfoMessage(result.payload);
    } else {
      setErrorMessage(result.payload);
    }
  };

  const password = watch('password');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h1>Регистрация</h1>
      <UiInput
        label="Email"
        type="email"
        autoComplete="email"
        error={errors.email?.message}
        {...register('email', {
          required: 'Введите email',
          pattern: { value: /\S+@\S+\.\S+/, message: 'Некорректный email' },
        })}
      />
      <UiInput
        label="Пароль"
        type="password"
        autoComplete="new-password"
        error={errors.password?.message}
        {...register('password', {
          required: 'Введите пароль',
          minLength: { value: 6, message: 'Не меньше 6 символов' },
        })}
      />
      <UiInput
        label="Повторите пароль"
        type="password"
        autoComplete="new-password"
        error={errors.passwordRepeat?.message}
        {...register('passwordRepeat', {
          required: 'Повторите пароль',
          validate: (v) => v === password || 'Пароли не совпадают',
        })}
      />
      {errorMessage && <span className="form-error">{errorMessage}</span>}
      {infoMessage && (
        <div className="banner banner--success">
          {infoMessage}
          <div style={{ marginTop: 8 }}>
            <UiLinkButton type="button" variant="link" onClick={onLogin}>
              Перейти ко входу
            </UiLinkButton>
          </div>
        </div>
      )}
      <UiButton type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Отправляем…' : 'Зарегистрироваться'}
      </UiButton>
    </form>
  );
}

// ----- Forgot -----

function ForgotForm({ onLogin }) {
  const dispatch = useDispatch();
  const [infoMessage, setInfoMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { email: '' } });

  const onSubmit = async (values) => {
    setInfoMessage(null);
    setErrorMessage(null);
    const result = await dispatch(forgotOwnerPassword(values));
    if (forgotOwnerPassword.fulfilled.match(result)) {
      setInfoMessage(result.payload);
    } else {
      setErrorMessage(result.payload);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h1>Восстановление пароля</h1>
      <UiInput
        label="Email"
        type="email"
        autoComplete="email"
        error={errors.email?.message}
        {...register('email', {
          required: 'Введите email',
          pattern: { value: /\S+@\S+\.\S+/, message: 'Некорректный email' },
        })}
      />
      {errorMessage && <span className="form-error">{errorMessage}</span>}
      {infoMessage && (
        <div className="banner banner--info">
          {infoMessage}
          <div style={{ marginTop: 8 }}>
            <UiLinkButton type="button" variant="link" onClick={onLogin}>
              Вернуться ко входу
            </UiLinkButton>
          </div>
        </div>
      )}
      <UiButton type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Отправляем…' : 'Отправить инструкцию'}
      </UiButton>
    </form>
  );
}
